// Imports: local files.
import ApiClient from '../../services/ApiClient';
import { showSnackbar, dispatchLoading, dispatchSuccess, dispatchError, getAxiosError } from '../../utils/redux';

// Name of property to be used in reducer data.
const manufacturersName = 'manufacturers';
const manufacturerName = 'manufacturer';

// Action Types: Get All Manufacturers.
export const GET_MANUFACTURERS_START = 'GET_MANUFACTURERS_START';
export const GET_MANUFACTURERS_SUCCESS = 'GET_MANUFACTURERS_SUCCESS';
export const GET_MANUFACTURERS_FAILED = 'GET_MANUFACTURERS_FAILED';
export const GET_MANUFACTURERS_RESET = 'GET_MANUFACTURERS_RESET';

// Action Types: Get One Manufacturer.
export const GET_MANUFACTURER_START = 'GET_MANUFACTURER_START';
export const GET_MANUFACTURER_SUCCESS = 'GET_MANUFACTURER_SUCCESS';
export const GET_MANUFACTURER_FAILED = 'GET_MANUFACTURER_FAILED';
export const GET_MANUFACTURER_RESET = 'GET_MANUFACTURER_RESET';

// Action Types: Create Manufacturer.
export const CREATE_MANUFACTURER_START = 'CREATE_MANUFACTURER_START';
export const CREATE_MANUFACTURER_SUCCESS = 'CREATE_MANUFACTURER_SUCCESS';
export const CREATE_MANUFACTURER_FAILED = 'CREATE_MANUFACTURER_FAILED';
export const CREATE_MANUFACTURER_RESET = 'CREATE_MANUFACTURER_RESET';

// Action Types: Update Manufacturer.
export const UPDATE_MANUFACTURER_START = 'UPDATE_MANUFACTURER_START';
export const UPDATE_MANUFACTURER_SUCCESS = 'UPDATE_MANUFACTURER_SUCCESS';
export const UPDATE_MANUFACTURER_FAILED = 'UPDATE_MANUFACTURER_FAILED';
export const UPDATE_MANUFACTURER_RESET = 'UPDATE_MANUFACTURER_RESET';

// Action Types: Delete Manufacturer.
export const DELETE_MANUFACTURER_START = 'DELETE_MANUFACTURER_START';
export const DELETE_MANUFACTURER_SUCCESS = 'DELETE_MANUFACTURER_SUCCESS';
export const DELETE_MANUFACTURER_FAILED = 'DELETE_MANUFACTURER_FAILED';
export const DELETE_MANUFACTURER_RESET = 'DELETE_MANUFACTURER_RESET';

// Action Creators: Get All Manufacturers.
const getManufacturersStart = (payload) => ({ type: GET_MANUFACTURERS_START, payload });
const getManufacturersSuccess = (payload) => ({ type: GET_MANUFACTURERS_SUCCESS, payload });
const getManufacturersFailed = (payload) => ({ type: GET_MANUFACTURERS_FAILED, payload });
const getManufacturersReset = () => ({ type: GET_MANUFACTURERS_RESET });

// Action Creators: Get One Manufacturer.
const getManufacturerStart = (payload) => ({ type: GET_MANUFACTURER_START, payload });
const getManufacturerSuccess = (payload) => ({ type: GET_MANUFACTURER_SUCCESS, payload });
const getManufacturerFailed = (payload) => ({ type: GET_MANUFACTURER_FAILED, payload });
const getManufacturerReset = () => ({ type: GET_MANUFACTURER_RESET });

// Action Creators: Create Manufacturer.
const createManufacturerStart = (payload) => ({ type: CREATE_MANUFACTURER_START, payload });
const createManufacturerSuccess = (payload) => ({ type: CREATE_MANUFACTURER_SUCCESS, payload });
const createManufacturerFailed = (payload) => ({ type: CREATE_MANUFACTURER_FAILED, payload });
const createManufacturerReset = () => ({ type: CREATE_MANUFACTURER_RESET });

// Action Creators: Update Manufacturer.
const updateManufacturerStart = (payload) => ({ type: UPDATE_MANUFACTURER_START, payload });
const updateManufacturerSuccess = (payload) => ({ type: UPDATE_MANUFACTURER_SUCCESS, payload });
const updateManufacturerFailed = (payload) => ({ type: UPDATE_MANUFACTURER_FAILED, payload });
const updateManufacturerReset = () => ({ type: UPDATE_MANUFACTURER_RESET });

// Action Creators: Delete Manufacturer.
const deleteManufacturerStart = (payload) => ({ type: DELETE_MANUFACTURER_START, payload });
const deleteManufacturerSuccess = (payload) => ({ type: DELETE_MANUFACTURER_SUCCESS, payload });
const deleteManufacturerFailed = (payload) => ({ type: DELETE_MANUFACTURER_FAILED, payload });
const deleteManufacturerReset = () => ({ type: DELETE_MANUFACTURER_RESET });

// Actions: Get All Manufacturers.
export const getManufacturers = (payload, options = {}) => {
  const { page, limit, pagination, name } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, getManufacturersStart);

      const query = { page, limit, pagination, name };
      const result = await ApiClient.get('/manufacturers', { params: query });
      if (result.data?.success) {
        const { manufacturers } = result.data?.data;
        dispatchSuccess(dispatch, getManufacturersSuccess, manufacturers, manufacturersName);
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, getManufacturersFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, getManufacturersFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Get All Manufacturers.
export const clearManufacturers = () => getManufacturersReset();

// Actions: Get One Manufacturer.
export const getManufacturer = (payload, options = {}) => {
  const { manufacturerId } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, getManufacturerStart);

      const result = await ApiClient.get(`/manufacturers/${manufacturerId}`);
      if (result.data?.success) {
        const { manufacturer } = result.data?.data;
        dispatchSuccess(dispatch, getManufacturerSuccess, manufacturer, manufacturerName);
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, getManufacturerFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, getManufacturerFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Get One Manufacturer.
export const clearManufacturer = () => getManufacturerReset();

// Actions: Create Manufacturer.
export const createManufacturer = (payload, options = {}) => {
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, createManufacturerStart);

      const fields = ['name', 'description', 'link', 'isSponsor', 'tools', 'logo'];
      const formData = new FormData();
      fields.forEach((field) => payload[field] && formData.append(field, payload[field]));

      const result = await ApiClient.post(`/manufacturers`, formData);
      if (result.data?.success) {
        const { manufacturer } = result.data?.data;
        dispatchSuccess(dispatch, createManufacturerSuccess, manufacturer, manufacturerName);
        showSnackbar(dispatch, 'Manufacturer created successfully!', 'success');
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, createManufacturerFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, createManufacturerFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Created Manufacturer.
export const clearCreatedManufacturer = () => createManufacturerReset();

// Actions: Update Manufacturer.
export const updateManufacturer = (payload, options = {}) => {
  const { manufacturerId } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, updateManufacturerStart);

      const fields = ['name', 'description', 'link', 'isSponsor', 'tools', 'logo'];
      const formData = new FormData();
      fields.forEach((field) => payload[field] && formData.append(field, payload[field]));

      const result = await ApiClient.put(`/manufacturers/${manufacturerId}`, formData);
      if (result.data?.success) {
        const { manufacturer } = result.data?.data;
        dispatchSuccess(dispatch, updateManufacturerSuccess, manufacturer, manufacturerName);
        showSnackbar(dispatch, 'Manufacturer updated successfully!', 'success');
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, updateManufacturerFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, updateManufacturerFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Updated Manufacturer.
export const clearUpdatedManufacturer = () => updateManufacturerReset();

// Actions: Delete Manufacturer.
export const deleteManufacturer = (payload, options = {}) => {
  const { manufacturerId } = payload;
  const {} = options;
  return async (dispatch) => {
    try {
      dispatchLoading(dispatch, deleteManufacturerStart);

      const result = await ApiClient.delete(`/manufacturers/${manufacturerId}`);
      if (result.data?.success) {
        const { manufacturer } = result.data?.data;
        dispatchSuccess(dispatch, deleteManufacturerSuccess, manufacturer, manufacturerName);
      } else {
        const errorMessage = result.data?.error || 'Something went wrong!';
        dispatchError(dispatch, deleteManufacturerFailed, errorMessage);
        showSnackbar(dispatch, errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage = getAxiosError(error);
      dispatchError(dispatch, deleteManufacturerFailed, errorMessage);
      showSnackbar(dispatch, errorMessage, 'error');
    }
  };
};

// Actions: Clear Deleted Manufacturer.
export const clearDeletedManufacturer = () => deleteManufacturerReset();
